import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SideBar from "../../../common/SideBar";
import HeaderBar from "../../../common/HeaderBar";
import ToastMessage from "../../../../utils/toasts/toastMessage";
import Loader from "../../../../utils/loader/loader.js";
import { formatDateandTime } from "../../../../utils/utils.js";
import CreateCohortModal from "./CohortModal";
import "./Cohort.scss";
import { profileDetails } from "../../../../Redux/profile/profile.action.js";
import {
  allUsersList,
  getUserList,
  teacherDetails,
} from "../../../../Redux/authentication/auth.action.js";
import {
  deleteTeacherCohort,
  getTeacherCohort,
} from "../../../../Redux/Teacher_Panel/cohort/cohort.action.js";
import DeleteConfirmModal from "../../../common/deleteConfirmModal/deleteConfirmModal.js";

const PAGE_SIZE_OPTIONS = [3, 6, 12, 18];
const DEFAULT_PAGE_SIZE = 3;

// Builds [1, 2, 3, "…", 10] style page lists
const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  let start = Math.max(2, current - 1);
  let end = Math.min(total - 1, current + 1);
  if (current <= 3) {
    start = 2;
    end = 4;
  } else if (current >= total - 2) {
    start = total - 3;
    end = total - 1;
  }

  const items = [1];
  if (start > 2) items.push("start-ellipsis");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("end-ellipsis");
  items.push(total);
  return items;
};

const ROLE_LABELS = {
  1: "Student",
  2: "Teacher",
  3: "Admin",
};

const CohortList = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterCohortName, setFilterCohortName] = useState("");

  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const teacherProfileDetails = useSelector(
    (state) => state?.ProfileManager?.user_details,
  );
  const teacherDetail = useSelector(
    (state) => state?.AuthManager?.teacherDetails,
  );
  const cohortStore =
    useSelector((s) => s?.CohortManager?.cohortListTeacher?.groups) || [];

  const allUsersStore = useSelector(
    (state) => state?.AuthManager?.usersList?.users,
  );

  console.log(allUsersStore, "allUsersStore");

  useEffect(() => {
    dispatch(profileDetails());
    dispatch(getTeacherCohort());
    dispatch(allUsersList());
  }, [dispatch]);

  const usersById = useMemo(() => {
    const map = {};
    (allUsersStore || []).forEach((u) => {
      map[String(u.user_id)] = u;
    });
    return map;
  }, [allUsersStore]);

  // ------------------------ Ownership ---------------------------------------
  const currentUserId = teacherDetail?.teacher_id;
  const normalize = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();
  const currentUserName = normalize(teacherDetail?.teacher_name);

  // True only if the logged-in teacher created this cohort.
  // Handles created_by being a string, a number, or an object.
  const isOwner = (cohort) => {
    if (!teacherDetail) return false;

    const creator = cohort?.created_by;

    // 1. Separate id field, if the API sends one
    const explicitId =
      cohort?.created_by_id ?? cohort?.teacher_id ?? cohort?.creator_id;
    if (explicitId != null && currentUserId != null) {
      return String(explicitId) === String(currentUserId);
    }

    // 2. created_by is an object, e.g. { id, name }
    if (creator && typeof creator === "object") {
      const objId = creator.id ?? creator.teacher_id ?? creator.user_id;
      if (objId != null && currentUserId != null) {
        return String(objId) === String(currentUserId);
      }
      const objName = creator.name ?? creator.teacher_name ?? creator.full_name;
      return normalize(objName) === currentUserName;
    }

    // 3. created_by is a number, so compare with the teacher id
    if (typeof creator === "number") {
      return currentUserId != null && String(creator) === String(currentUserId);
    }

    // 4. created_by is a name string
    return normalize(creator) === currentUserName;
  };

  // // Returns { name, role } for the "Created By" column
  // const getCreator = (cohort) => {
  //   const raw = cohort?.created_by;
  //   const id =
  //     cohort?.created_by_id ??
  //     (raw && typeof raw === "object" ? raw.id ?? raw.user_id : raw);

  //   const user = usersById[String(id)];
  //   if (user) {
  //     const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  //     return {
  //       name: name || user.email || "—",
  //       role: ROLE_LABELS[user.role_id] || "",
  //     };
  //   }

  //   // Fallback: API already sent a name string
  //   if (typeof raw === "string" && Number.isNaN(Number(raw))) {
  //     return { name: raw, role: "" };
  //   }
  //   return { name: "—", role: "" };
  // };

  const getCreator = (cohort) => {
    // Logged-in teacher created it, so show "Self"
    if (isOwner(cohort)) {
      return { name: "Self", role: "", isSelf: true };
    }

    const raw = cohort?.created_by;
    const id =
      cohort?.created_by_id ??
      (raw && typeof raw === "object" ? (raw.id ?? raw.user_id) : raw);

    const user = usersById[String(id)];
    if (user) {
      const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      return {
        name: name || user.email || "—",
        role: ROLE_LABELS[user.role_id] || "",
        isSelf: false,
      };
    }

    if (typeof raw === "string" && Number.isNaN(Number(raw))) {
      return { name: raw, role: "", isSelf: false };
    }
    return { name: "—", role: "", isSelf: false };
  };

  useEffect(() => {
    if (teacherProfileDetails) {
      dispatch(teacherDetails(teacherProfileDetails?.id));
    }
  }, [teacherProfileDetails, dispatch]);

  const classDivisionMap = useMemo(() => {
    const map = {};
    (teacherProfileDetails?.classes || []).forEach(
      ({ grade, division, subject }) => {
        if (!map[grade]) map[grade] = {};
        map[grade][division] = subject || [];
      },
    );
    return map;
  }, [teacherProfileDetails]);

  const classOptions = useMemo(
    () =>
      Object.keys(classDivisionMap).map((grade) => ({
        label: grade,
        value: grade,
      })),
    [classDivisionMap],
  );

  // ------------------------ Filters logic -----------------------------------
  const cohortList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return cohortStore.filter((c) => {
      if (!q) return true;
      const classLabel = `${c?.grade || ""}${c?.division || ""}`.toLowerCase();
      const cohortName = (c?.group_name || "").toLowerCase();
      const creatorName = getCreator(c).name.toLowerCase();
      const selfName = c && isOwner(c) ? currentUserName : "";
      return (
        classLabel.includes(q) ||
        cohortName.includes(q) ||
        creatorName.includes(q) ||
        selfName.includes(q)
      );
    });
  }, [cohortStore, searchQuery, filterClass, filterCohortName, usersById]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterClass, filterCohortName]);

  const classFilterOptions = useMemo(() => {
    const set = new Set();
    cohortStore.forEach((c) => {
      if (c?.grade && c?.division) set.add(`${c.grade}|${c.division}`);
    });
    return Array.from(set)
      .map((key) => {
        const [grade, division] = key.split("|");
        return { value: key, label: `${grade}${division}`, grade, division };
      })
      .sort(
        (a, b) =>
          a.grade.localeCompare(b.grade, undefined, { numeric: true }) ||
          a.division.localeCompare(b.division),
      );
  }, [cohortStore]);

  const cohortNameFilterOptions = useMemo(() => {
    const set = new Set();
    cohortStore.forEach((c) => c?.group_name && set.add(c.group_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cohortStore]);

  // ------------------------ Pagination logic --------------------------------
  const totalItems = cohortList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const paginatedList = useMemo(
    () => cohortList.slice(startIndex, startIndex + pageSize),
    [cohortList, startIndex, pageSize],
  );

  const pageItems = useMemo(
    () => getPageItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const goToPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // ------------------------ Handlers ----------------------------------------
  const handleOpenCreate = () => {
    setSelectedCohort(null);
    setEditing(false);
    setViewOnly(false);
    setShowModal(true);
  };

  // Owners get the editable modal, everyone else gets read-only
  const handleOpenMembers = (cohort) => {
    setSelectedCohort(cohort);
    setEditing(true);
    setViewOnly(!isOwner(cohort));
    setShowModal(true);
  };

  const handleDelete = (e, cohort) => {
    e.preventDefault();
    if (!isOwner(cohort)) return; // safety guard
    setSelectedCohortId(cohort.id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCohortId) return;
    setLoading(true);
    try {
      const result = await dispatch(deleteTeacherCohort(selectedCohortId));
      if (result?.success) {
        await dispatch(getTeacherCohort());
        setShowConfirmModal(false);
        setToastState({
          show: true,
          message: result?.message || "Cohort deleted successfully.",
          type: "error",
        });
      } else {
        setToastState({
          show: true,
          message: result?.message || "Unable to delete cohort.",
          type: "error",
        });
      }
    } catch {
      setToastState({
        show: true,
        message: "Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setSelectedCohortId(null);
    }
  };

  const handleCohortSaved = () => {
    setPage(1); // newest cohort is first
    dispatch(getTeacherCohort());
  };

  const hasCohorts = totalItems > 0;

  return (
    <main className="ff2 main-page">
      <HeaderBar />
      <div className="overflow-hidden inner-page-content">
        <SideBar />

        <div className="create-selection-container">
          {hasCohorts || cohortStore.length > 0 ? (
            <div className="cohort-list-wrapper">
              <div className="sticky-header-section">
                <h5>Cohorts: {totalItems}</h5>
                <div className="button-group">
                  <button className="btn-add-test" onClick={handleOpenCreate}>
                    + Add New Cohort
                  </button>
                </div>
              </div>

              {/* Filters row */}
              <div className="cohort-filters-row">
                <div className="cohort-search-box">
                  <input
                    type="text"
                    placeholder="search cohort name or class..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="cohort-search-input"
                  />
                </div>

                <select
                  className="cohort-filter-select"
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {classFilterOptions.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>

                <select
                  className="cohort-filter-select"
                  value={filterCohortName}
                  onChange={(e) => setFilterCohortName(e.target.value)}
                >
                  <option value="">All Cohorts</option>
                  {cohortNameFilterOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                {(searchQuery || filterClass || filterCohortName) && (
                  <button
                    type="button"
                    className="btn-outlined btn-outlined--dark cohort-filter-clear"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterClass("");
                      setFilterCohortName("");
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="scrollable-content-box">
                <div className="list-gap-wrapper">
                  {paginatedList?.map((cohort) => {
                    const owner = isOwner(cohort);
                    const creator = getCreator(cohort);

                    return (
                      <div className="cohort-summary-bar" key={cohort.id}>
                        <div
                          className="cohort-badge"
                          title={`Class ${cohort?.grade}${cohort?.division}`}
                        >
                          <span className="cohort-badge-caption">Class</span>
                          <span className="cohort-badge-value">
                            {cohort?.grade}
                            {cohort?.division}
                          </span>
                        </div>

                        <div className="cohort-field cohort-field--name">
                          <span className="cohort-field-label">
                            Cohort Name
                          </span>
                          <span
                            className="cohort-field-value"
                            title={cohort?.group_name}
                          >
                            {cohort?.group_name || "—"}
                          </span>
                        </div>

                        <div className="cohort-field cohort-field--created-by">
                          <span className="cohort-field-label">Created By</span>
                          <span
                            className={`cohort-field-value ${creator.isSelf ? "is-self" : ""}`}
                            title={`${creator.name}${creator.role ? ` (${creator.role})` : ""}`}
                          >
                            {creator.name}
                            {creator.role && (
                              <span
                                className={`role-pill role-pill--${creator.role.toLowerCase()}`}
                              >
                                {creator.role}
                              </span>
                            )}
                          </span>
                        </div>

                        <div className="cohort-field cohort-field--students">
                          <span className="cohort-field-label">Students</span>
                          <span className="cohort-field-value">
                            <span className="students-pill">
                              {(cohort?.student_id || []).length}
                            </span>
                          </span>
                        </div>

                        <div className="cohort-field cohort-field--created">
                          <span className="cohort-field-label">Created At</span>
                          <span className="cohort-field-value">
                            {formatDateandTime(cohort?.created_at)}
                          </span>
                        </div>

                        <div className="cohort-actions">
                          <button
                            className="btn-outlined btn-outlined--dark"
                            onClick={() => handleOpenMembers(cohort)}
                          >
                            {owner ? "Edit Members" : "View Members"}
                          </button>
                          <button
                            className="btn-outlined btn-outlined--dark"
                            onClick={(e) => handleDelete(e, cohort)}
                            disabled={!owner}
                            title={
                              owner
                                ? ""
                                : "Only the creator can delete this cohort"
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination footer */}
              <div className="pagination-bar">
                <div className="pagination-info">
                  Showing <strong>{totalItems ? startIndex + 1 : 0}</strong>–
                  <strong>{endIndex}</strong> of <strong>{totalItems}</strong>
                </div>

                <nav className="pagination-controls" aria-label="Cohort pages">
                  <button
                    className="page-btn"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                  >
                    «
                  </button>
                  <button
                    className="page-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>

                  {pageItems.map((item) =>
                    typeof item === "number" ? (
                      <button
                        key={item}
                        className={`page-btn ${
                          item === currentPage ? "active" : ""
                        }`}
                        onClick={() => goToPage(item)}
                        aria-current={item === currentPage ? "page" : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={item} className="page-ellipsis">
                        …
                      </span>
                    ),
                  )}

                  <button
                    className="page-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                  <button
                    className="page-btn"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                  >
                    »
                  </button>
                </nav>

                <label className="page-size">
                  Rows per page
                  <select value={pageSize} onChange={handlePageSizeChange}>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : (
            <div className="empty-state-wrapper">
              <div className="empty-state-header">
                <h2 className="title">Create your first cohort</h2>
              </div>

              <div className="cards-wrapper">
                <div
                  className="selection-card test-card"
                  onClick={handleOpenCreate}
                >
                  <div className="icon">👥</div>
                  <h5>Add New Cohort</h5>
                  <p>Pick a class and division, then assign students</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DeleteConfirmModal
          show={showConfirmModal}
          message="Are you sure you want to delete this cohort?"
          loading={loading}
          onCancel={() => {
            setShowConfirmModal(false);
            setSelectedCohortId(null);
          }}
          onConfirm={handleConfirmDelete}
        />

        <Loader show={loading} />

        <ToastMessage
          show={toastState.show}
          onClose={() => setToastState({ show: false, message: "", type: "" })}
          message={toastState.message}
          type={toastState.type}
        />
      </div>

      <CreateCohortModal
        show={showModal}
        onHide={() => setShowModal(false)}
        classOptions={classOptions}
        classDivisionMap={classDivisionMap}
        teacherClasses={teacherDetail?.classes}
        cohortData={selectedCohort}
        editing={editing}
        viewOnly={viewOnly}
        onSaved={handleCohortSaved}
        teacherSchoolName={teacherProfileDetails?.school}
      />
    </main>
  );
};

export default CohortList;
